<template>
    <div class="supplement-recommendations">
        <div class="section-header">
            <h3>Evidence-Based Supplements</h3>
            <span class="info-badge" @click="showInfo = true">ℹ️</span>
        </div>

        <p class="section-intro">
            Supplements recommended by sports science research for football players.
            Always consult a medical professional before starting any supplement.
        </p>

        <!-- Context Banner -->
        <div class="context-banner" :class="activityContext">
            <span class="context-icon">{{ contextIcon }}</span>
            <div class="context-info">
                <span class="context-label">{{ contextLabel }}</span>
                <span class="context-advice">{{ contextAdvice }}</span>
            </div>
        </div>

        <!-- Primary Recommendations -->
        <div class="recommendation-category">
            <h4>
                <span class="category-icon">⭐</span>
                Primary Recommendations
            </h4>
            <p class="category-desc">Strong evidence supporting use for football players</p>

            <div class="supplement-list">
                <div v-for="supp in primarySupplements" :key="supp.id" class="supplement-card"
                    :class="{ expanded: expandedSupp === supp.id }" @click="toggleExpand(supp.id)">
                    <div class="supp-header">
                        <div class="supp-icon" :style="{ backgroundColor: supp.color }">
                            {{ supp.icon }}
                        </div>
                        <div class="supp-info">
                            <span class="supp-name">{{ supp.name }}</span>
                            <span class="supp-benefit">{{ supp.shortBenefit }}</span>
                        </div>
                        <div class="supp-timing" v-if="getTimingForContext(supp)">
                            <span class="timing-badge">{{ getTimingForContext(supp) }}</span>
                        </div>
                    </div>

                    <div v-if="expandedSupp === supp.id" class="supp-details">
                        <div class="detail-section">
                            <h5>How It Helps</h5>
                            <p>{{ supp.description }}</p>
                        </div>

                        <div class="detail-section">
                            <h5>Dosage</h5>
                            <div class="dosage-info">
                                <div class="dosage-main">
                                    <span class="dosage-value">{{ supp.dosage.amount }}</span>
                                    <span class="dosage-unit">{{ supp.dosage.unit }}</span>
                                </div>
                                <span class="dosage-frequency">{{ supp.dosage.frequency }}</span>
                            </div>
                        </div>

                        <div class="detail-section">
                            <h5>Best Timing</h5>
                            <div class="timing-options">
                                <div v-for="timing in supp.timing" :key="timing.when" class="timing-option"
                                    :class="{ recommended: isRecommendedTiming(timing, supp) }">
                                    <span class="timing-when">{{ timing.when }}</span>
                                    <span class="timing-note">{{ timing.note }}</span>
                                </div>
                            </div>
                        </div>

                        <div class="detail-section evidence">
                            <h5>Evidence Level</h5>
                            <div class="evidence-bar">
                                <div class="evidence-fill" :style="{ width: supp.evidenceLevel + '%' }"></div>
                            </div>
                            <span class="evidence-label">{{ getEvidenceLabel(supp.evidenceLevel) }}</span>
                        </div>

                        <div v-if="supp.cautions" class="detail-section cautions">
                            <h5>⚠️ Cautions</h5>
                            <ul>
                                <li v-for="caution in supp.cautions" :key="caution">{{ caution }}</li>
                            </ul>
                        </div>
                    </div>

                    <div class="expand-indicator">
                        <span>{{ expandedSupp === supp.id ? '▲' : '▼' }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Secondary Recommendations -->
        <div class="recommendation-category secondary">
            <h4>
                <span class="category-icon">✓</span>
                Secondary Options
            </h4>
            <p class="category-desc">Moderate evidence, may benefit some players</p>

            <div class="supplement-list compact">
                <div v-for="supp in secondarySupplements" :key="supp.id" class="supplement-card compact"
                    :class="{ expanded: expandedSupp === supp.id }" @click="toggleExpand(supp.id)">
                    <div class="supp-header">
                        <div class="supp-icon small" :style="{ backgroundColor: supp.color }">
                            {{ supp.icon }}
                        </div>
                        <div class="supp-info">
                            <span class="supp-name">{{ supp.name }}</span>
                            <span class="supp-benefit">{{ supp.shortBenefit }}</span>
                        </div>
                    </div>

                    <div v-if="expandedSupp === supp.id" class="supp-details">
                        <div class="detail-section">
                            <h5>How It Helps</h5>
                            <p>{{ supp.description }}</p>
                        </div>

                        <div class="detail-section">
                            <h5>Dosage</h5>
                            <div class="dosage-info">
                                <span class="dosage-value">{{ supp.dosage.amount }} {{ supp.dosage.unit }}</span>
                                <span class="dosage-frequency">{{ supp.dosage.frequency }}</span>
                            </div>
                        </div>

                        <div class="detail-section evidence">
                            <h5>Evidence Level</h5>
                            <div class="evidence-bar">
                                <div class="evidence-fill" :style="{ width: supp.evidenceLevel + '%' }"></div>
                            </div>
                            <span class="evidence-label">{{ getEvidenceLabel(supp.evidenceLevel) }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Match Day Protocol -->
        <div v-if="activityContext === 'match_day'" class="match-day-protocol">
            <h4>
                <span class="category-icon">⚽</span>
                Match Day Protocol
            </h4>

            <div class="protocol-timeline">
                <div class="protocol-item">
                    <div class="protocol-time">-3 to -4 hrs</div>
                    <div class="protocol-content">
                        <span class="protocol-what">Caffeine (3-6 mg/kg)</span>
                        <span class="protocol-note">With pre-match meal</span>
                    </div>
                </div>

                <div class="protocol-item">
                    <div class="protocol-time">-30 min</div>
                    <div class="protocol-content">
                        <span class="protocol-what">Beetroot juice / Nitrate</span>
                        <span class="protocol-note">For endurance benefit</span>
                    </div>
                </div>

                <div class="protocol-item">
                    <div class="protocol-time">Half-time</div>
                    <div class="protocol-content">
                        <span class="protocol-what">Carb gel or drink</span>
                        <span class="protocol-note">30-60g carbs</span>
                    </div>
                </div>

                <div class="protocol-item">
                    <div class="protocol-time">Post-match</div>
                    <div class="protocol-content">
                        <span class="protocol-what">Protein + Carbs</span>
                        <span class="protocol-note">Within 30-60 min</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Daily Checklist -->
        <div class="daily-checklist">
            <h4>
                <span class="category-icon">📋</span>
                Today's Supplement Checklist
            </h4>

            <div class="checklist-items">
                <div v-for="item in dailyChecklist" :key="item.id" class="checklist-item"
                    :class="{ completed: item.completed }" @click="toggleChecklist(item.id)">
                    <div class="check-box">
                        <span v-if="item.completed">✓</span>
                    </div>
                    <div class="check-info">
                        <span class="check-name">{{ item.name }}</span>
                        <span class="check-timing">{{ item.timing }}</span>
                    </div>
                    <span class="check-dose">{{ item.dose }}</span>
                </div>
            </div>
        </div>

        <!-- Info Modal -->
        <div v-if="showInfo" class="modal-overlay" @click.self="showInfo = false">
            <div class="info-modal">
                <div class="modal-header">
                    <h3>About These Recommendations</h3>
                    <button class="close-btn" @click="showInfo = false">×</button>
                </div>

                <div class="modal-content">
                    <div class="info-section">
                        <h5>Evidence-Based Approach</h5>
                        <p>
                            These recommendations are based on peer-reviewed sports science research,
                            including systematic reviews and meta-analyses published in journals like
                            the British Journal of Sports Medicine, Journal of the International
                            Society of Sports Nutrition, and Medicine & Science in Sports & Exercise.
                        </p>
                    </div>

                    <div class="info-section">
                        <h5>Individual Variation</h5>
                        <p>
                            Responses to supplements vary between individuals. What works for one
                            player may not work for another. Always start with lower doses to assess
                            tolerance.
                        </p>
                    </div>

                    <div class="info-section warning">
                        <h5>⚠️ Important Disclaimer</h5>
                        <p>
                            This information is for educational purposes only and is not medical
                            advice. Consult with a sports dietitian or medical professional before
                            starting any supplement regimen. Ensure all supplements are batch-tested
                            and certified (e.g., Informed Sport) to avoid contamination risks.
                        </p>
                    </div>

                    <div class="info-section">
                        <h5>For Youth Players</h5>
                        <p>
                            Supplement recommendations for players under 18 should be more
                            conservative. Focus on food-first nutrition strategies. Only basic
                            supplements like vitamin D (if deficient) and protein may be appropriate
                            under professional guidance.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "SupplementRecommendations",

    props: {
        activityContext: {
            type: String,
            default: "training_day",
        },
        playerWeight: {
            type: Number,
            default: 70,
        },
        playerAge: {
            type: Number,
            default: 18,
        },
    },

    data() {
        return {
            expandedSupp: null,
            showInfo: false,
            dailyChecklist: [
                { id: 1, name: "Creatine", timing: "Any time", dose: "5g", completed: false },
                { id: 2, name: "Vitamin D", timing: "With meal", dose: "1000-2000 IU", completed: false },
                { id: 3, name: "Omega-3", timing: "With meal", dose: "2-3g", completed: false },
                { id: 4, name: "Protein", timing: "Post-training", dose: "20-40g", completed: false },
            ],
            primarySupplements: [
                {
                    id: "creatine",
                    name: "Creatine Monohydrate",
                    icon: "⚡",
                    color: "#6f42c1",
                    shortBenefit: "Power & high-intensity performance",
                    description:
                        "Creatine is one of the most researched sports supplements. It enhances phosphocreatine stores in muscles, improving performance in repeated high-intensity efforts like sprints, jumps, and tackles.",
                    dosage: { amount: "3-5", unit: "g/day", frequency: "Daily (no loading needed)" },
                    timing: [
                        { when: "Post-training", note: "Optimal for muscle uptake" },
                        { when: "With carbs", note: "Enhances absorption" },
                        { when: "Any time", note: "Consistency matters most" },
                    ],
                    evidenceLevel: 95,
                    cautions: [
                        "May cause minor water retention initially",
                        "Ensure adequate hydration",
                        "Use pure creatine monohydrate",
                    ],
                },
                {
                    id: "caffeine",
                    name: "Caffeine",
                    icon: "☕",
                    color: "#795548",
                    shortBenefit: "Focus, endurance & reaction time",
                    description:
                        "Caffeine improves alertness, reduces perceived exertion, and can enhance both endurance and high-intensity performance. Effects are well-documented in football-specific studies.",
                    dosage: { amount: "3-6", unit: "mg/kg", frequency: "Pre-match/training" },
                    timing: [
                        { when: "60-90 min before", note: "Peak blood levels" },
                        { when: "Half-time top-up", note: "Optional 1-2 mg/kg" },
                    ],
                    evidenceLevel: 90,
                    cautions: [
                        "Can disrupt sleep if taken late",
                        "Individual tolerance varies widely",
                        "Avoid exceeding 6 mg/kg",
                        "May cause anxiety in some",
                    ],
                },
                {
                    id: "protein",
                    name: "Protein (Whey/Casein)",
                    icon: "🥛",
                    color: "#17a2b8",
                    shortBenefit: "Muscle recovery & growth",
                    description:
                        "Protein supplementation helps meet daily requirements for muscle protein synthesis, especially important post-training. Whey is fast-absorbing; casein is slow-release.",
                    dosage: { amount: "20-40", unit: "g", frequency: "Per serving, 3-4x daily" },
                    timing: [
                        { when: "Post-training", note: "Within 2 hours ideal" },
                        { when: "Before bed", note: "Casein for overnight recovery" },
                        { when: "Between meals", note: "To hit daily target" },
                    ],
                    evidenceLevel: 95,
                    cautions: [
                        "Whole foods preferred when possible",
                        "Check for additives/quality",
                        "Lactose-free options available",
                    ],
                },
                {
                    id: "vitamin-d",
                    name: "Vitamin D",
                    icon: "☀️",
                    color: "#ffc107",
                    shortBenefit: "Bone health, immunity & muscle function",
                    description:
                        "Many athletes in northern latitudes are deficient in Vitamin D, especially in winter. It supports bone health, immune function, and may influence muscle strength and recovery.",
                    dosage: { amount: "1000-4000", unit: "IU/day", frequency: "Daily with fat-containing meal" },
                    timing: [
                        { when: "With breakfast/lunch", note: "Better absorption with food" },
                    ],
                    evidenceLevel: 85,
                    cautions: [
                        "Get levels tested before high-dose supplementation",
                        "Don't exceed 4000 IU without medical advice",
                    ],
                },
            ],
            secondarySupplements: [
                {
                    id: "omega3",
                    name: "Omega-3 (Fish Oil)",
                    icon: "🐟",
                    color: "#20c997",
                    shortBenefit: "Anti-inflammatory & brain health",
                    description:
                        "Omega-3 fatty acids (EPA/DHA) may help reduce exercise-induced inflammation, support joint health, and optimize brain function.",
                    dosage: { amount: "2-3", unit: "g EPA+DHA/day", frequency: "Daily with meals" },
                    evidenceLevel: 70,
                },
                {
                    id: "beetroot",
                    name: "Beetroot Juice / Nitrate",
                    icon: "🥤",
                    color: "#dc3545",
                    shortBenefit: "Endurance & oxygen efficiency",
                    description:
                        "Dietary nitrate from beetroot juice can improve endurance performance by enhancing muscle efficiency and blood flow.",
                    dosage: { amount: "400-500", unit: "mg nitrate", frequency: "2-3 hrs before exercise" },
                    evidenceLevel: 75,
                },
                {
                    id: "beta-alanine",
                    name: "Beta-Alanine",
                    icon: "💪",
                    color: "#fd7e14",
                    shortBenefit: "Buffer lactic acid during high-intensity",
                    description:
                        "Beta-alanine increases muscle carnosine, which buffers acid during high-intensity exercise. May benefit repeated sprint performance.",
                    dosage: { amount: "3-6", unit: "g/day", frequency: "Daily in divided doses" },
                    evidenceLevel: 65,
                },
                {
                    id: "collagen",
                    name: "Collagen + Vitamin C",
                    icon: "🦴",
                    color: "#6c757d",
                    shortBenefit: "Joint & tendon support",
                    description:
                        "Collagen peptides combined with vitamin C may support connective tissue health and injury prevention when taken before training.",
                    dosage: { amount: "10-15", unit: "g collagen", frequency: "30-60 min before training" },
                    evidenceLevel: 60,
                },
            ],
        };
    },

    computed: {
        contextLabel() {
            const labels = {
                match_day: "Match Day",
                carb_loading: "Carb Loading Day",
                training_day: "Training Day",
                recovery_day: "Recovery Day",
                rest_day: "Rest Day",
            };
            return labels[this.activityContext] || "Training Day";
        },

        contextIcon() {
            const icons = {
                match_day: "⚽",
                carb_loading: "🍝",
                training_day: "🏃",
                recovery_day: "🔄",
                rest_day: "🛋️",
            };
            return icons[this.activityContext] || "🏃";
        },

        contextAdvice() {
            const advice = {
                match_day: "Focus on caffeine timing and carb fueling strategy",
                carb_loading: "Prioritize carbs, maintain creatine and vitamin D",
                training_day: "Normal supplement protocol with post-training protein",
                recovery_day: "Protein and omega-3 for recovery, consider collagen",
                rest_day: "Maintain baseline supplements, focus on nutrition from food",
            };
            return advice[this.activityContext] || "";
        },
    },

    methods: {
        toggleExpand(id) {
            this.expandedSupp = this.expandedSupp === id ? null : id;
        },

        toggleChecklist(id) {
            const item = this.dailyChecklist.find((i) => i.id === id);
            if (item) {
                item.completed = !item.completed;
            }
        },

        getTimingForContext(supp) {
            if (this.activityContext === "match_day") {
                if (supp.id === "caffeine") return "Pre-match";
                if (supp.id === "protein") return "Post-match";
            }
            if (this.activityContext === "training_day") {
                if (supp.id === "protein") return "Post-training";
            }
            return null;
        },

        isRecommendedTiming(timing, supp) {
            if (this.activityContext === "match_day" && supp.id === "caffeine") {
                return timing.when === "60-90 min before";
            }
            if (supp.id === "protein") {
                return timing.when === "Post-training";
            }
            return false;
        },

        getEvidenceLabel(level) {
            if (level >= 90) return "Strong Evidence";
            if (level >= 75) return "Good Evidence";
            if (level >= 60) return "Moderate Evidence";
            return "Emerging Evidence";
        },
    },
};
</script>

<style lang="scss" scoped>
.supplement-recommendations {
    background: white;
    border-radius: 12px;
    padding: 16px;
}

.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;

    h3 {
        margin: 0;
        font-size: 1.1rem;
        font-weight: 600;
    }

    .info-badge {
        cursor: pointer;
        font-size: 1.2rem;
    }
}

.section-intro {
    color: #666;
    font-size: 0.85rem;
    margin: 0 0 16px 0;
    line-height: 1.5;
}

.context-banner {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    border-radius: 10px;
    margin-bottom: 20px;

    &.match_day {
        background: linear-gradient(135deg, rgba(220, 53, 69, 0.1), rgba(253, 126, 20, 0.1));
        border-left: 3px solid #dc3545;
    }

    &.training_day {
        background: linear-gradient(135deg, rgba(0, 123, 255, 0.1), rgba(23, 162, 184, 0.1));
        border-left: 3px solid #007bff;
    }

    &.recovery_day {
        background: linear-gradient(135deg, rgba(40, 167, 69, 0.1), rgba(32, 201, 151, 0.1));
        border-left: 3px solid #28a745;
    }

    &.rest_day {
        background: rgba(108, 117, 125, 0.1);
        border-left: 3px solid #6c757d;
    }

    .context-icon {
        font-size: 1.5rem;
    }

    .context-info {
        .context-label {
            display: block;
            font-weight: 600;
        }

        .context-advice {
            font-size: 0.85rem;
            color: #666;
        }
    }
}

.recommendation-category {
    margin-bottom: 24px;

    h4 {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 0 0 4px 0;
        font-size: 1rem;

        .category-icon {
            font-size: 1.1rem;
        }
    }

    .category-desc {
        color: #666;
        font-size: 0.8rem;
        margin: 0 0 12px 0;
    }

    &.secondary {
        opacity: 0.9;
    }
}

.supplement-list {
    display: flex;
    flex-direction: column;
    gap: 10px;

    &.compact {
        gap: 8px;
    }
}

.supplement-card {
    background: #f8f9fa;
    border-radius: 10px;
    padding: 12px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background: #f0f0f0;
    }

    &.expanded {
        background: white;
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
    }

    &.compact {
        padding: 10px;
    }

    .supp-header {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .supp-icon {
        width: 40px;
        height: 40px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.2rem;
        flex-shrink: 0;

        &.small {
            width: 32px;
            height: 32px;
            font-size: 1rem;
        }
    }

    .supp-info {
        flex: 1;

        .supp-name {
            display: block;
            font-weight: 600;
            font-size: 0.95rem;
        }

        .supp-benefit {
            font-size: 0.8rem;
            color: #666;
        }
    }

    .supp-timing {
        .timing-badge {
            padding: 4px 10px;
            background: rgba(40, 167, 69, 0.15);
            color: #28a745;
            border-radius: 12px;
            font-size: 0.75rem;
            font-weight: 600;
        }
    }

    .supp-details {
        margin-top: 16px;
        padding-top: 16px;
        border-top: 1px solid #eee;

        .detail-section {
            margin-bottom: 14px;

            &:last-child {
                margin-bottom: 0;
            }

            h5 {
                margin: 0 0 8px 0;
                font-size: 0.85rem;
                font-weight: 600;
                color: #555;
            }

            p {
                margin: 0;
                font-size: 0.85rem;
                line-height: 1.5;
                color: #666;
            }

            &.evidence {
                .evidence-bar {
                    height: 6px;
                    background: #e0e0e0;
                    border-radius: 3px;
                    overflow: hidden;
                    margin-bottom: 4px;

                    .evidence-fill {
                        height: 100%;
                        background: linear-gradient(90deg, #28a745, #20c997);
                        border-radius: 3px;
                    }
                }

                .evidence-label {
                    font-size: 0.75rem;
                    color: #28a745;
                    font-weight: 500;
                }
            }

            &.cautions {
                background: #fff3cd;
                padding: 12px;
                border-radius: 8px;
                margin-top: 16px;

                h5 {
                    color: #856404;
                }

                ul {
                    margin: 0;
                    padding-left: 16px;

                    li {
                        font-size: 0.8rem;
                        color: #856404;
                        margin-bottom: 4px;
                    }
                }
            }
        }
    }

    .dosage-info {
        display: flex;
        align-items: baseline;
        gap: 12px;

        .dosage-main {
            .dosage-value {
                font-size: 1.3rem;
                font-weight: 700;
                color: #333;
            }

            .dosage-unit {
                font-size: 0.9rem;
                color: #666;
            }
        }

        .dosage-frequency {
            font-size: 0.8rem;
            color: #999;
        }
    }

    .timing-options {
        display: flex;
        flex-direction: column;
        gap: 6px;

        .timing-option {
            display: flex;
            justify-content: space-between;
            padding: 8px 10px;
            background: #f8f9fa;
            border-radius: 6px;
            font-size: 0.85rem;

            &.recommended {
                background: rgba(40, 167, 69, 0.1);
                border: 1px solid rgba(40, 167, 69, 0.3);

                .timing-when {
                    color: #28a745;
                    font-weight: 600;
                }
            }

            .timing-when {
                font-weight: 500;
            }

            .timing-note {
                color: #666;
                font-size: 0.8rem;
            }
        }
    }

    .expand-indicator {
        text-align: center;
        margin-top: 8px;
        color: #999;
        font-size: 0.8rem;
    }
}

.match-day-protocol {
    background: linear-gradient(135deg, #f8f9fa, #e9ecef);
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 20px;

    h4 {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 0 0 16px 0;
        font-size: 1rem;
    }

    .protocol-timeline {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    .protocol-item {
        display: flex;
        gap: 12px;
        padding: 10px;
        background: white;
        border-radius: 8px;

        .protocol-time {
            width: 80px;
            font-weight: 700;
            color: #dc3545;
            flex-shrink: 0;
        }

        .protocol-content {
            .protocol-what {
                display: block;
                font-weight: 600;
            }

            .protocol-note {
                font-size: 0.8rem;
                color: #666;
            }
        }
    }
}

.daily-checklist {
    background: #f8f9fa;
    border-radius: 12px;
    padding: 16px;

    h4 {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 0 0 12px 0;
        font-size: 1rem;
    }

    .checklist-items {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .checklist-item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 10px 12px;
        background: white;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.2s;

        &:hover {
            background: #f0f0f0;
        }

        &.completed {
            .check-box {
                background: #28a745;
                border-color: #28a745;
                color: white;
            }

            .check-name {
                text-decoration: line-through;
                color: #999;
            }
        }

        .check-box {
            width: 24px;
            height: 24px;
            border: 2px solid #ddd;
            border-radius: 6px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 0.9rem;
            flex-shrink: 0;
        }

        .check-info {
            flex: 1;

            .check-name {
                display: block;
                font-weight: 500;
            }

            .check-timing {
                font-size: 0.75rem;
                color: #666;
            }
        }

        .check-dose {
            font-size: 0.85rem;
            color: #666;
            font-weight: 500;
        }
    }
}

.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
}

.info-modal {
    background: white;
    border-radius: 16px;
    width: 100%;
    max-width: 500px;
    max-height: 90vh;
    overflow-y: auto;

    .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px;
        border-bottom: 1px solid #eee;

        h3 {
            margin: 0;
            font-size: 1.1rem;
        }

        .close-btn {
            width: 32px;
            height: 32px;
            border-radius: 50%;
            border: none;
            background: #f0f0f0;
            font-size: 1.2rem;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }

    .modal-content {
        padding: 16px;

        .info-section {
            margin-bottom: 20px;

            &:last-child {
                margin-bottom: 0;
            }

            h5 {
                margin: 0 0 8px 0;
                font-size: 0.95rem;
                font-weight: 600;
            }

            p {
                margin: 0;
                font-size: 0.9rem;
                line-height: 1.6;
                color: #555;
            }

            &.warning {
                background: #fff3cd;
                padding: 14px;
                border-radius: 10px;
                border-left: 4px solid #ffc107;

                h5 {
                    color: #856404;
                }

                p {
                    color: #856404;
                }
            }
        }
    }
}
</style>
